import { useEffect } from "react";
import { Button, Link, Box } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import callIcon from "../../../../../_assets/images/agnostic-flow/call-icon.svg";
import { useFormContext } from "../../../../common/contexts/FormContext";
import {
  AppConstants,
  BACK_BUTTON_TEXT,
  formActionButtonTypes,
  sharedStateKeys,
  SUMBIT_AND_CONTINUE_BUTTON_TEXT,
  ONLY_SUBMIT_BTN_TEXT,
  stepTypes,
  windowCallLink,
  PROTYPE_COMMERCIAL_TEXT,
  conceptIDs,
  SCHEDULE_SERVICE,
  IS_CONTACT_ME_BUTTON,
  CONTACT_ME_BUTTON_TEXT,
  MLY_IDS,
} from "../../../../constants/constant";
import { SchedulerConfig } from "../../../../Interface/scheduler/form";
import "./FormActionButtons.css";
import { useTheme } from "@mui/material/styles";
import { getButtonStyles, getButtonLayout } from "./FormActionButtons.utils";
import { useSharedStateWithActions } from "../../../../common/hooks/useSharedState";
import DisclaimerTerms from "../DisclaimerTerms/DisclaimerTerms";
import useIsMobile from "../../../../common/hooks/useIsMobile";
import { sessionStorageUtils } from "../../../../utility/utility";

const PRICE_QUOTE_NO = "no";

const FormActionButtons = ({
  currentStep,
  totalUpdatedSteps,
  handleNext,
  handleSubmit,
  handleSkipAndSubmit,
  getTotalUpdatedSteps,
  online,
  formConfig,
  handleBack,
  currentStepType,
  enableCallBack,
}: {
  currentStep: number;
  totalUpdatedSteps: number;
  handleNext: () => void;
  handleSubmit: () => void;
  handleSkipAndSubmit: () => void;
  getTotalUpdatedSteps: () => number;
  online: boolean;
  formConfig: SchedulerConfig;
  handleBack: () => void;
  currentStepType: string;
  enableCallBack: boolean;
}) => {
  const { formValues, setFormValues } = useFormContext();
  const theme = useTheme();
  const conceptCode = formConfig.conceptCode;
  const { state, setValue } = useSharedStateWithActions();

  const isMobile = useIsMobile();
  const conceptId = formConfig.conceptid;

  //setting the value to false when the price quote card page isn't visible and still IS_QUOTE_CARD_VISIBLE is true
  useEffect(() => {
    if (
      currentStepType !== stepTypes.STEP_PRICE_QUOTE_ESTIMATION &&
      state[sharedStateKeys.IS_QUOTE_CARD_VISIBLE]
    ) {
      setValue(sharedStateKeys.IS_QUOTE_CARD_VISIBLE, false);
    }
  }, []);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        event.preventDefault();
        buttonAction();
      }
    };

    document.addEventListener("keypress", handleKeyPress);

    return () => {
      document.removeEventListener("keypress", handleKeyPress);
    };
  }, [currentStep, totalUpdatedSteps, formConfig, formValues]);

  const handleContactMeButtonClick = (navigateToConfirmation: boolean) => {
    if (navigateToConfirmation) {
      sessionStorageUtils.setItem(IS_CONTACT_ME_BUTTON, navigateToConfirmation);
    } else {
      sessionStorageUtils.removeItem(IS_CONTACT_ME_BUTTON);
    }
    handleNext();
  };

  const isPriceQuoteCardVisible = state[sharedStateKeys.IS_QUOTE_CARD_VISIBLE];
  const buttonStyles = getButtonStyles(
    theme,
    formConfig.conceptid,
    currentStep,
    formConfig.steps,
    isPriceQuoteCardVisible,
  );

  const isMobileText = isMobile ? "mobile" : "desktop";
  const ctaLabels = formConfig?.steps[currentStep]?.ctaButtonLabels || {};
  const authoredSubmitLabel =
    ctaLabels.submitButton?.label || SUMBIT_AND_CONTINUE_BUTTON_TEXT;
  const submitHide = ctaLabels.submitButton?.hideButton ?? false;

  const authoredBackLabel = ctaLabels.backButton?.label || BACK_BUTTON_TEXT;
  const backHide = ctaLabels.backButton?.hideButton ?? false;

  const authoredSkipLabel = ctaLabels.skipAndSubmitButton?.label;
  const skipHide =
    formConfig.steps[currentStep]?.steptype === AppConstants.CONTACT_US_TYPE
      ? true
      : (ctaLabels.skipAndSubmitButton?.hideButton ?? true); //By default it is hidden

  const authoredWindowCallLinkLabel =
    ctaLabels?.callUs?.label || windowCallLink;
  const windowCallLinkHide = ctaLabels.callUs?.hideButton ?? false;

  const isLastStep = currentStep === getTotalUpdatedSteps();
  const isBeforeLastStep = currentStep < getTotalUpdatedSteps();
  const isPriceQuoteNo = formValues?.pricequoteselection === PRICE_QUOTE_NO;

  const hidePriceQuoteSection =
    !formValues?.priceQuoteEstimateAvailable &&
    state[`${sharedStateKeys.IS_PRICE_QUOTE_PAGE}`];

  // check if contactMeButton define as empty string or undefined
  const contactMeHideButton =
    ctaLabels?.contactMeButton?.label === "" ||
    ctaLabels.contactMeButton === undefined
      ? true
      : (ctaLabels.contactMeButton?.hideButton ?? false);
  const contactMeButtonText =
    ctaLabels?.contactMeButton?.label || CONTACT_ME_BUTTON_TEXT;

  const handleNavigationToConfirmation = () => {
    const { navigateToConfirmation } =
      formConfig?.steps[currentStep]?.ctaButtonLabels?.submitButton ?? {};
    if (navigateToConfirmation) {
      sessionStorageUtils.setItem(IS_CONTACT_ME_BUTTON, navigateToConfirmation);
    }
  };

  const handleWDGButtonAction = () => {
    const isCommercialWithStep =
      formValues?.proptype === PROTYPE_COMMERCIAL_TEXT && currentStep > 0;
    return isLastStep || isPriceQuoteNo || isCommercialWithStep;
  };

  const isLAPConceptWithPayment = () => {
    const isLAPConcept = formConfig.conceptid === conceptIDs.LAP_CONCEPT_ID;
    const isReachStep = currentStepType === stepTypes.HOW_WE_CAN_REACH;
    return isLAPConcept && isReachStep;
  };

  const handleJUKButtonAction = () => {
    return currentStepType === AppConstants.REQUEST_TIME_TYPE || isLastStep;
  };

  const returnButtonAction = () => {
    handleNavigationToConfirmation();
    if (formConfig.conceptid === conceptIDs.WDG_CONCEPT_ID) {
      return handleWDGButtonAction();
    }
    if (
      formConfig.conceptid === conceptIDs.JUK_CONCEPT_ID &&
      (formValues?.estimatedPrice === "--" || enableCallBack)
    ) {
      return handleJUKButtonAction();
    }
    if (isLAPConceptWithPayment()) {
      return formValues?.proceedWithoutPayment === "true";
    }
    return isLastStep;
  };

  const getMLYButtonText = () => {
    if (currentStep !== 0 && currentStepType !== stepTypes.CONTACT_US) {
      return null;
    }
    const formConfigValues = formConfig.steps[currentStep];
    const frequency = formValues;
    const serviceType = formValues?.["servicetype"];
    switch (serviceType) {
      case MLY_IDS.COMMERCIAL:
        if (
          formValues[MLY_IDS.COMMERCIAL_FREQUENCY] ===
          MLY_IDS.COMMERCIAL_ONE_TIME_CLEAN
        ) {
          return formConfigValues.ctaButtonLabels?.submitButton
            ?.OneTimeCleanCtaLabel;
        }
        return formConfigValues.ctaButtonLabels?.submitButton
          ?.RecurringCtaLabel;
      case MLY_IDS.RESIDENTIAL:
        if (frequency[MLY_IDS.FREQUENCY] === MLY_IDS.ONE_TIME_CLEAN) {
          return formConfigValues.ctaButtonLabels?.submitButton
            ?.OneTimeCleanCtaLabel;
        }
        return formConfigValues.ctaButtonLabels?.submitButton
          ?.RecurringCtaLabel;
      default:
        return SUMBIT_AND_CONTINUE_BUTTON_TEXT;
    }
  };

  const getWDGButtonText = () => {
    if (
      online &&
      formValues?.existingcustomer === "true" &&
      formValues.proptype !== PROTYPE_COMMERCIAL_TEXT &&
      currentStepType === stepTypes.STEP_PRICE_QUOTE_REQUEST
    ) {
      return SCHEDULE_SERVICE;
    }

    if (!online && currentStepType === stepTypes.STEP_PRICE_QUOTE_ESTIMATION) {
      return ONLY_SUBMIT_BTN_TEXT;
    }
    if (
      (hidePriceQuoteSection && currentStep > 0) ||
      formValues.proptype === PROTYPE_COMMERCIAL_TEXT
    ) {
      return ONLY_SUBMIT_BTN_TEXT;
    }
    return priceQuoteButtonText || authoredSubmitLabel;
  };

  const returnButtonText = () => {
    if (formConfig.conceptid === conceptIDs.MLY_CONCEPT_ID) {
      return getMLYButtonText() || authoredSubmitLabel;
    }

    if (formConfig.conceptid === conceptIDs.WDG_CONCEPT_ID) {
      return getWDGButtonText();
    }

    return authoredSubmitLabel;
  };

  const handleBackButtonClick = (currentStep: number) => {
    if (formValues?.pricequoteselection === PRICE_QUOTE_NO) {
      setFormValues((prev) => ({
        ...prev,
        pricequoteselection: "",
      }));
    }
    //WDG Price Quote Soft Back Feature
    if (
      state[sharedStateKeys.IS_QUOTE_CARD_VISIBLE] &&
      currentStepType === stepTypes.STEP_PRICE_QUOTE_ESTIMATION
    ) {
      setValue(sharedStateKeys.IS_QUOTE_CARD_VISIBLE, false);
      setValue(sharedStateKeys.PRICE_QUOTE_ESTIMATION_OBJECT, []); //Clear the quote estimation data on back
      setValue(sharedStateKeys.NON_PRICABLE_QUOTE_ESTIMATION_OBJECT, []);
      return;
    }
    if (currentStep <= 0) {
      if (globalThis.history.length > 1) {
        globalThis.history.back();
      } else {
        globalThis.location.href = "/";
      }
    } else {
      handleBack();
    }
  };

  const buttonAction = returnButtonAction() ? handleSubmit : handleNext;

  const priceQuoteButtonText = isPriceQuoteNo
    ? (state[`${sharedStateKeys.PRICE_QUOTE_SELECTION_DETAILS}`]
        ?.submitButtonName as string) || authoredSubmitLabel
    : authoredSubmitLabel;

  const showArrowIcon =
    (isBeforeLastStep && !isPriceQuoteNo) || isPriceQuoteCardVisible;

  const renderBackButton = (buttonType?: string) => {
    if (backHide) return null;
    return (
      <Button
        onClick={() => handleBackButtonClick(currentStep)}
        className={`back back-${isMobileText}-${conceptCode}`}
        id="back-btn"
        sx={buttonStyles.back}
      >
        {buttonType === formActionButtonTypes.BACK_WITH_LESS_THAN_ARROW_ICON &&
          renderLessThanArrowIcon()}
        {authoredBackLabel}
      </Button>
    );
  };
  const shouldHideSubmitButton = () => {
    if (currentStepType === AppConstants.SERVICE_BOT_SECTION) {
      return true;
    }

    if (
      formConfig.conceptid === conceptIDs.LAP_CONCEPT_ID &&
      formValues?.servicetype === AppConstants.COMMERCIAL_TEXT
    ) {
      return true;
    }

    return (
      (submitHide && !isPriceQuoteCardVisible) ||
      !(isBeforeLastStep || isLastStep || isPriceQuoteNo)
    );
  };

  const isSubmitBtnDisabled = () => {
    const buttonText = returnButtonText();

    if (
      conceptId === conceptIDs.LAP_CONCEPT_ID &&
      buttonText === SUMBIT_AND_CONTINUE_BUTTON_TEXT
    ) {
      return (
        !formValues?.proceedWithoutPayment ||
        formValues?.proceedWithoutPayment === "false"
      );
    }

    // Disable submit button for LAP concept on "how we can reach" step until interactive map data is received
    if (
      conceptId === conceptIDs.LAP_CONCEPT_ID &&
      currentStepType === stepTypes.HOW_WE_CAN_REACH &&
      !state[sharedStateKeys.IS_INTERACTIVE_MAP_READY]
    ) {
      return true;
    }

    return false;
  };

  const getSubmitButtonClassName = () => {
    return conceptCode === "ASV"
      ? `submit-${conceptCode} submit-btn-${currentStep + 1}`
      : `submit-${conceptCode}`;
  };

  const renderSubmitButton = () => {
    if (shouldHideSubmitButton()) {
      return null;
    }

    const { navigateToConfirmation } =
      formConfig?.steps[currentStep]?.ctaButtonLabels?.submitButton || {};
    const buttonClassName = getSubmitButtonClassName();

    return (
      <Button
        id="submit-btn"
        className={buttonClassName}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
        onClick={() => buttonAction()}
        color={isBeforeLastStep ? "secondary" : undefined}
        sx={buttonStyles.submit}
        disabled={isSubmitBtnDisabled()}
      >
        {returnButtonText()}
        {!navigateToConfirmation && showArrowIcon && (
          <ArrowForwardIosOutlinedIcon
            className={`greater-than-arrow-icon-${conceptCode}`}
            sx={buttonStyles.greaterThanArrowIcon}
          />
        )}
      </Button>
    );
  };

  const renderSkipLink = () => {
    if (skipHide || windowCallLinkHide) return null;
    return (
      <Link
        onClick={handleSkipAndSubmit}
        color="inherit"
        id="skip-and-submit"
        className={`skip-${isMobileText}-${conceptCode}`}
        sx={buttonStyles.skip}
      >
        <Box className="window-call-me-link">
          {state[sharedStateKeys.IS_QUOTE_CARD_VISIBLE] &&
            currentStepType === stepTypes.STEP_PRICE_QUOTE_ESTIMATION && (
              <img src={callIcon} alt="" />
            )}
          {state[sharedStateKeys.IS_QUOTE_CARD_VISIBLE] &&
          currentStepType === stepTypes.STEP_PRICE_QUOTE_ESTIMATION
            ? authoredWindowCallLinkLabel
            : authoredSkipLabel}
        </Box>
      </Link>
    );
  };

  const renderLessThanArrowIcon = () => {
    return (
      <Box className={`less-than-arrow-icon-wrap-${conceptCode}`}>
        <ArrowBackIosIcon
          className={`less-than-arrow-icon-${conceptCode}`}
          sx={{
            color: (theme) => theme.typography.body1.arrowIconColor,
            "&:hover": {
              color: (theme) => theme.typography.body1.arrowIconColor,
            },
          }}
        />
      </Box>
    );
  };

  // render ContactMe Button
  const renderContactMeButton = () => {
    if (contactMeHideButton) return null;
    const { navigateToConfirmation } =
      formConfig?.steps[currentStep]?.ctaButtonLabels?.contactMeButton || {};
    return (
      <Button
        className={`contact-me-${conceptCode}`}
        id="contact-me-btn"
        onClick={() => handleContactMeButtonClick(navigateToConfirmation)}
        sx={buttonStyles.contactMe}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
      >
        {contactMeButtonText}
        {!navigateToConfirmation && (
          <ArrowForwardIosOutlinedIcon sx={buttonStyles.greaterThanArrowIcon} />
        )}
      </Button>
    );
  };

  const renderButtons = () => {
    const layout = getButtonLayout(
      isMobile,
      formConfig,
      currentStep,
      conceptId,
      state[sharedStateKeys.IS_QUOTE_CARD_VISIBLE],
    );
    return layout?.map((button) => {
      switch (button) {
        case formActionButtonTypes.SUBMIT:
          return renderSubmitButton();
        case formActionButtonTypes.BACK:
          return renderBackButton();
        case formActionButtonTypes.SKIP:
          return isMobile && !skipHide ? (
            <Box
              className={`skip-link-wrap ${state[sharedStateKeys.IS_QUOTE_CARD_VISIBLE] ? "skip-table" : "skip-service"}`}
              key="skip"
            >
              {renderSkipLink()}
            </Box>
          ) : (
            renderSkipLink()
          );
        case formActionButtonTypes.BACK_WITH_LESS_THAN_ARROW_ICON:
          return renderBackButton(button);

        case formActionButtonTypes.CONTACT_ME:
          if (
            formConfig?.steps[currentStep]?.ctaButtonLabels?.contactMeButton
              ?.hideButton === false
          ) {
            return renderContactMeButton();
          }
          break;
        default:
          return null;
      }
    });
  };

  return (
    <div
      className={
        "button-wrap " + (isMobile ? "mobile-view" : "") + " button-new-wrap"
      }
    >
      {
        <>
          <DisclaimerTerms
            currentStepType={currentStepType}
            elementId={
              isMobile ? "disclaimer-terms-mobile" : "disclaimer-terms-desktop"
            }
          />
          {renderButtons()}
        </>
      }
    </div>
  );
};

export default FormActionButtons;
